import React, { ReactNode } from "react";
import styled from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  FigureLarge,
  InfoLabel,
  Meta,
} from "features/affiliation/NewLook/roles";
import { AFFILIATION_CONTAINER } from "features/affiliation/NewLook/newLookUtils";

export interface FigureItem {
  key: string;
  label: ReactNode;
  value: ReactNode;
  meta?: ReactNode;
  tooltip?: ReactNode;
}

interface FiguresPanelProps {
  title: ReactNode;
  seeAllTo?: string;
  seeAllLabel?: string;
  items: FigureItem[];
  className?: string;
  "data-testid"?: string;
}

/**
 * A titled panel of a few secondary figures side by side (label, figure,
 * meta), split by hairlines; one row per figure on phones.
 */
const FiguresPanel = ({
  title,
  seeAllTo,
  seeAllLabel,
  items,
  className,
  "data-testid": testId,
}: FiguresPanelProps) => (
  <Panel
    className={className}
    title={title}
    seeAllTo={seeAllTo}
    seeAllLabel={seeAllLabel}
    flush={true}
    data-testid={testId}
  >
    <Figures $count={items.length}>
      {items.map((item) => (
        <Item key={item.key}>
          <InfoLabel
            tooltip={item.tooltip}
            analyticsFieldName={
              typeof item.label === "string" ? item.label : item.key
            }
          >
            {item.label}
          </InfoLabel>
          <FigureLarge>{item.value}</FigureLarge>
          {item.meta && <Meta>{item.meta}</Meta>}
        </Item>
      ))}
    </Figures>
  </Panel>
);

export default FiguresPanel;

const Figures = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  height: 100%;
  @container ${AFFILIATION_CONTAINER} (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
/* Hairlines between the figures; rows split by hairlines on phones. */
const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 16px 20px 20px;
  & + & {
    border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  @container ${AFFILIATION_CONTAINER} (max-width: 560px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas: "label figure" "meta figure";
    align-items: center;
    column-gap: 12px;
    row-gap: 2px;
    padding: 12px 16px 12px 20px;
    & + & {
      border-left: none;
      border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    }
    > :nth-child(1) {
      grid-area: label;
    }
    > :nth-child(2) {
      grid-area: figure;
      font-size: 17px;
      line-height: 22px;
    }
    > :nth-child(3) {
      grid-area: meta;
    }
  }
`;
